import posthog from 'posthog-js';
import {
  clearImpersonationSession,
  readTokenFromFragment,
  storeImpersonationSession,
} from './impersonation';

/**
 * Consumes `#impersonationToken=…` and hard-redirects, returning true when the
 * caller must skip the rest of its bootstrap.
 *
 * Call this as the very first thing in `main.tsx`, above `initDash0` and any
 * analytics init. `history.replaceState` is not enough: the browser's own
 * `PerformanceNavigationTiming` entry for *this* page load already captured the
 * full URL, fragment included, and Dash0's navigation instrumentation reports it
 * verbatim. Redirecting before telemetry starts means it never sees this load —
 * the fresh navigation has no fragment.
 *
 * `posthog.reset()` drops any identity the operator's own earlier session left in
 * this browser, so the customer's activity is not attributed to them.
 */
export const consumeImpersonationFragment = (): boolean => {
  const token = readTokenFromFragment(window.location.hash);
  if (!token) return false;

  const stored = storeImpersonationSession(token);

  // A rejected token must not leave an earlier session in place: the bearer
  // resolver prefers any stored impersonation token over Cognito, so the operator
  // would silently keep acting as the previous target.
  if (!stored) {
    clearImpersonationSession();
  }

  try {
    posthog.reset();
  } catch {
    // PostHog is not initialised in every app/env; leaving is still correct.
  }

  window.location.replace(stored ? `/${window.location.search}` : `/auth${window.location.search}`);
  return true;
};
